import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client from "@/lib/api";
import { FeedPathTag } from "@/components/FeedPathTag";
import { Pencil, Save, Share2 } from "lucide-react";
import { VersionTag } from "@/components/VersionTag";

function Field({ label, feedPath, className = "", children }) {
  return (
    <div className={className}>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        <FeedPathTag path={feedPath} />
      </Label>
      {children}
    </div>
  );
}

const FIELDS = [
  ["facebook", "Facebook", "Dealer.SocialMedia.Facebook"],
  ["instagram", "Instagram", "Dealer.SocialMedia.Instagram"],
  ["youtube", "YouTube", "Dealer.SocialMedia.YouTube"],
  ["twitter", "Twitter", "Dealer.SocialMedia.Twitter"],
  ["pinterest", "Pinterest", "Dealer.SocialMedia.Pinterest"],
  ["linkedin", "LinkedIn", "Dealer.SocialMedia.Linkedin"],
  ["tiktok", "TikTok", "Dealer.SocialMedia.TikTok"],
];

/** Social media links — synced from the feed every import run; editing here
 * is a local override that lasts until the next sync (same as Branch address). */
export function SocialMediaCard({ dealer, dealerId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft({ ...(dealer.social_media || {}) });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/social-media`, draft);
      onSaved(data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const v = (k) => (editing ? draft?.[k] ?? "" : dealer.social_media?.[k] || "");

  return (
    <Card className="mb-6 p-6" data-testid="social-media-card">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">Social Media <VersionTag version={dealer.card_versions?.social_media} testId="version-tag-social_media" /></h2>
        </div>
        {!editing ? (
          <Button data-testid="edit-social-media-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button data-testid="save-social-media-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              Save
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {FIELDS.map(([key, label, feedPath]) => (
          <Field key={key} label={label} feedPath={feedPath}>
            <Input
              data-testid={`social-media-${key}`}
              disabled={!editing || saving}
              value={v(key)}
              placeholder="https://…"
              className="font-mono text-xs"
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          </Field>
        ))}
      </div>
    </Card>
  );
}
